import * as omitBy from 'lodash/omitBy'
import * as memoize from 'lodash/memoize'
import { PeergroupRelation } from 'interfaces/peergroups'
import {
  PostUid,
  ComposeId,
  RawFeedUser,
  FeedUser,
  RawComment,
  Comment,
  FeedUserUid,
  RawPost,
  Post,
  RawOwnComment,
  RawMentions,
  Mention,
  FeedOrdering,
  ComposeState,
} from 'interfaces/feed'
import { FeedPeergroupQuery, FeedFilterQuery } from 'interfaces/feed'
import { encodeUrlParams } from 'helpers/utilsHelpers'
import { Anchor } from 'interfaces/navigation'
import { FEED_POSTS_URL } from 'tangle-shared'
import { ImageURLs } from 'interfaces/media'
import { UserImage } from 'interfaces/userImage'
import { normalize } from 'helpers/normalizeHelpers'
import { RawCursorPaginatable, CursorPaginatable, RawForwardPaginatable } from 'interfaces/table'
import { NormalizedPostsResponse, NormalizedCommentsResponse } from 'actions/feedActions'
import { preparePostForDisplay, getReplyComposeId } from 'helpers/composeHelpers'
import { SelectableFeedFilterPeergroup } from 'interfaces/feed'
import { SocialInsight } from 'interfaces/socialInsights'

export const getRelationFilter = memoize((relation: PeergroupRelation): SelectableFeedFilterPeergroup => ({
  type: 'peergroup',
  relationId: relation.id,
}))

export const getFeedFilterPeergroupQuery = (relation: PeergroupRelation): FeedPeergroupQuery => {
  const { peergroupId, slug } = relation

  return {
    filter_by: 'peergroup',
    id: peergroupId,
    loose: slug === 'peers',
  }
}

export const getFeedCommentAnchorId = (id: ComposeId | PostUid): Anchor => ({
  anchor: 'feedpost_comment',
  id,
})

export const getFeedPostAnchorId = (uid: PostUid): Anchor => ({
  anchor: 'feedposts_post',
  id: uid,
})

export const getSinglePostAnchorId = (uid: PostUid): Anchor => ({
  anchor: 'feedpost',
  id: uid,
})

export const getScrollAnchor = (composeState: ComposeState, isDesktop: boolean) => {
  const {
    type,
    postUid,
    isEdit,
    replyCommentUid,
    commentUid,
  } = composeState

  if (type === 'post') {
    if (!postUid) {
      return 'feedposts_compose'
    } else if (isEdit && !isDesktop) {
      return getFeedPostAnchorId(postUid)
    }
  } else if (type === 'comment') {
    if (replyCommentUid) {
      return getFeedCommentAnchorId(getReplyComposeId(replyCommentUid))
    } else if (commentUid) {
      return getFeedCommentAnchorId(commentUid)
    } else if (postUid) {
      return getFeedCommentAnchorId(postUid)
    }
  }
  return null
}

export const serializeFeedFilterQuery = (params: FeedFilterQuery): string =>
  encodeUrlParams(omitBy(params, (value: any) => typeof value === 'undefined'))

export const isFeedPage = () => new RegExp(`\\${ FEED_POSTS_URL }\\b`).test(location.pathname)
export const isFeedPostsPage = () => {
  return location.pathname === FEED_POSTS_URL
}

// normalizers

const normalizeMentions = (mentions: RawMentions) => {
  return Object.keys(mentions).reduce<Mention[]>((acc, uid) => {
    acc.push({
      uid,
      ...mentions[uid],
    })
    return acc
  }, [])
}

export const normalizeImage = (image: ImageURLs): UserImage => ({
  id: image.id,
  image,

  isLoading: false,
  errorCode: null,
})

const normalizeAuthor = (rawAuthor: RawFeedUser) => {
  const { avatar, ...restAuthor } = rawAuthor
  const author: FeedUser = {
    ...restAuthor,
    avatarId: avatar ? avatar.id : null,
  }

  return {
    author,
    avatar: avatar ? normalizeImage(avatar) : null,
  }
}

export const normalizeOwnComment = (rawComment: RawOwnComment, postUid: PostUid, authorUid: FeedUserUid) => {
  const { images, text, mentions, likes_count = 0, ...restComment } = rawComment

  const comment: Comment = {
    ...restComment,
    likes_count,
    text: preparePostForDisplay(text, mentions),
    mentions: normalizeMentions(mentions),
    authorUid,
    postUid,
    imagesIds: images.map((image) => image.id),
    children: null,
    childrenCount: 0,
  }

  return {
    comment,
    images: images.map(normalizeImage),
  }
}

const normalizeComment = (rawComment: RawComment, postUid: PostUid) => {
  const { author, children, children_count, ...tempComment } = rawComment
  const authorUid = author.uid
  const { author: nextAuthor, avatar } = normalizeAuthor(author)

  const { comment, images } = normalizeOwnComment(tempComment, postUid, authorUid)
  const { likes_count = 0, ...restComment } = comment
  const nextComment: Comment = {
    ...restComment,
    likes_count,
    authorUid,
    postUid,
    imagesIds: images.map((image) => image.id),
    childrenCount: children_count || 0,
  }

  const comments = [nextComment]
  const users = [nextAuthor]

  if (avatar) {
    images.push(avatar)
  }

  if (children) {
    const {
      users: childrenUsers,
      comments: childrenComments,
      images: childrenImages,
      thread: childrenThread,
    } = normalizeCommentsIntermediate(children, postUid)

    images.push(...childrenImages)
    users.push(...childrenUsers)
    comments.push(...childrenComments)

    nextComment.children = childrenThread
  }

  return {
    comments,
    users,
    images,
  }
}

type NormalizeCommentsAcc = {
  users: FeedUser[]
  comments: Comment[]
  images: UserImage[]
}
const normalizeCommentsIntermediate = (rawComments: RawForwardPaginatable<RawComment>, postUid: PostUid) => {
  const { users, comments, images: nextImages } = rawComments.results.reduce<NormalizeCommentsAcc>((acc, item) => {

    const {
      comments: childrenComments,
      images: childrenImages,
      users: childrenUsers,
    } = normalizeComment(item, postUid)

    acc.users.push(...childrenUsers)
    acc.images.push(...childrenImages)
    acc.comments.push(...childrenComments)

    return acc
  }, {
    users: [],
    comments: [],
    images: [],
  })

  return {
    users,
    comments,
    images: nextImages,
    thread: {
      pagination: {
        previous: null,
        next: rawComments.next,
      },
      results: rawComments.results.map((comment) => comment.uid),
      isLoading: false,
      errorCode: null,
    },
  }
}

export const normalizeComments = (rawComments: RawForwardPaginatable<RawComment>, postUid: PostUid): NormalizedCommentsResponse => {
  const { users, comments, images: nextImages, thread } = normalizeCommentsIntermediate(rawComments, postUid)

  return {
    users: normalize(users, 'uid'),
    comments: normalize(comments, 'uid'),
    images: normalize(nextImages, 'id'),
    thread,
  }
}

export const normalizePost = (rawPost: RawPost) => {
  const {
    author,
    comments,
    comments_ordering,
    images: postImages,
    text,
    mentions,
    ...restPost
  } = rawPost

  const { author: nextAuthor, avatar: authorAvatar } = normalizeAuthor(author)

  const {
    images: childrenImages,
    comments: childrenComments,
    users: childrenUsers,
    thread: childrenThread,
  } = normalizeCommentsIntermediate(comments, rawPost.uid)

  const nextPost: Post = {
    ...restPost,
    text: preparePostForDisplay(text, mentions),
    mentions: normalizeMentions(mentions),
    authorUid: author.uid,
    comments: childrenThread,
    commentsOrdering: comments_ordering.join(',') as FeedOrdering,
    imagesIds: postImages.map((image) => image.id),
  }

  return {
    post: nextPost,
    comments: childrenComments,
    images: [
      ...postImages.map(normalizeImage),
      ...authorAvatar ? [authorAvatar] : [],
      ...childrenImages,
    ],
    users: [
      ...childrenUsers,
      nextAuthor,
    ],
  }
}

type IntermediatePostsResponse = {
  users: FeedUser[]
  comments: Comment[]
  posts: Post[]
  postsByFilter: CursorPaginatable<PostUid>
  images: UserImage[]
}
export function normalizePosts (postsPaginatable: RawCursorPaginatable<RawPost>): NormalizedPostsResponse {
  const { next, previous } = postsPaginatable

  const postResponse = postsPaginatable.results.reduce<IntermediatePostsResponse>((result, rawPost) => {
    const { post, comments, images, users } = normalizePost(rawPost)
    result.users.push(...users)
    result.comments.push(...comments)
    result.images.push(...images)
    result.posts.push(post)
    result.postsByFilter.results.push(post.uid)
    return result
  },
  {
    users: [],
    comments: [],
    posts: [],
    postsByFilter: {
      pagination: {
        next,
        previous,
      },
      results: [],
    },
    images: [],
  })

  return {
    users: normalize(postResponse.users, 'uid'),
    comments: normalize(postResponse.comments, 'uid'),
    posts: normalize(postResponse.posts, 'uid'),
    postsByFilter: postResponse.postsByFilter,
    images: normalize(postResponse.images, 'id'),
  }
}

export const getNextCounterValue = (prevCounter: number, toIncrement: boolean, isRequest: boolean): number => {
  const multiplier = isRequest ? 1 : -1
  const inc = toIncrement ? 1 : -1

  return prevCounter + multiplier * inc
}

export const isSocialInsightPost = (post: Post): post is SocialInsight => {
  return post.type === 'social_insight'
}

export const createEmptyThread = () => ({
  isLoading: false,
  errorCode: null,
  pagination: { next: null, previous: null },
  results: [],
})
